import React, { useEffect } from 'react';
import MarketingLayout from '../components/MarketingLayout';

const LAST_UPDATED = 'April 27, 2026';

export default function PrivacyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy — PitchFriendly';
  }, []);

  return (
    <MarketingLayout>
      <section className="bg-white border-b border-gray-100 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-400">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="space-y-8 text-sm text-gray-600 leading-relaxed">

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">1. Introduction</h2>
              <p>
                PitchFriendly ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service. Please read this policy carefully. If you disagree with its terms, please discontinue use of the Service.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
              <p className="mb-3"><strong className="text-gray-800">Information you provide:</strong></p>
              <ul className="list-disc pl-5 space-y-1 mb-4">
                <li>Account registration information (name, email address, password)</li>
                <li>Billing information (processed and stored by Stripe)</li>
                <li>Contact data and press lists you import or create</li>
                <li>Email content you compose and send via the Service</li>
                <li>Support communications and feedback</li>
              </ul>
              <p className="mb-3"><strong className="text-gray-800">Information collected automatically:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Usage data (features used, pages visited, actions taken)</li>
                <li>Log data (IP address, browser type, access timestamps)</li>
                <li>Email interaction data (opens, clicks) for emails sent via the Service</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Provide, maintain, and improve the Service</li>
                <li>Process payments and send billing-related communications</li>
                <li>Respond to customer support requests</li>
                <li>Send product updates and service announcements</li>
                <li>Monitor and analyze usage patterns to improve the Service</li>
                <li>Detect and prevent fraud, abuse, and security incidents</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">4. Information Sharing</h2>
              <p>We do not sell your personal information. We may share information with:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li><strong className="text-gray-800">Service providers</strong> who assist in operating the Service (e.g., cloud hosting, payment processing, email delivery). These providers are contractually bound to protect your data.</li>
                <li><strong className="text-gray-800">Legal authorities</strong> when required by law, court order, or governmental authority.</li>
                <li><strong className="text-gray-800">Business transfers</strong> in connection with a merger, acquisition, or sale of assets, with appropriate confidentiality protections.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">5. Data About Third Parties</h2>
              <p>
                The Service allows you to store contact information about journalists and other individuals. You are responsible for ensuring you have an appropriate legal basis for storing and processing this information. You should maintain your own privacy policy disclosures where required by applicable law.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">6. Data Retention</h2>
              <p>
                We retain your account data for as long as your account is active. After account closure, we retain data for 30 days before deletion, except where longer retention is required by law. Email tracking data (open/click events) is retained for up to 12 months.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">7. Security</h2>
              <p>
                We implement industry-standard security measures including encryption in transit (TLS) and at rest, access controls, and regular security reviews. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">8. Cookies and Tracking</h2>
              <p>
                We use cookies and similar tracking technologies for authentication (session management), analytics, and preference storage. You can configure your browser to refuse cookies; however, some parts of the Service may not function correctly without them.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">9. Your Rights</h2>
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Access the personal information we hold about you</li>
                <li>Correct inaccurate personal information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to or restrict certain processing activities</li>
                <li>Data portability (receive your data in a portable format)</li>
              </ul>
              <p className="mt-3">To exercise these rights, contact us at <a href="mailto:privacy@pitchfriendly.com" className="text-blue-600 hover:underline">privacy@pitchfriendly.com</a>.</p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">10. International Transfers</h2>
              <p>
                The Service is operated from the United States. If you are located outside the US, your information may be transferred to and processed in the US, where data protection laws may differ from your jurisdiction. By using the Service, you consent to this transfer.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">11. Children's Privacy</h2>
              <p>
                The Service is not directed at individuals under the age of 16. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us immediately.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy periodically. We will notify you of material changes by email or by posting a notice in the Service at least 14 days before the change takes effect. Continued use of the Service after the effective date constitutes acceptance of the updated policy.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">13. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or our privacy practices, contact us at <a href="mailto:privacy@pitchfriendly.com" className="text-blue-600 hover:underline">privacy@pitchfriendly.com</a>.
              </p>
            </div>

          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
